import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { usePageSEO } from "@/hooks/use-page-seo";
import Footer from "@/components/Footer";

const POSH_URL = "https://posh.vip/e/high-tide-celio-rooftop";

const PreParty = () => {
  usePageSEO({
    title: "High Tide · The Official YRD Pre-Party with Party Pupils | May 23, 2026",
    description:
      "Party Pupils take over the CieLo Rooftop at Cambria Detroit on Saturday, May 23, 2026 — the official Yacht Rock Detroit pre-party. $20 at the door, free for YRD VIP.",
    canonical: "https://yachtrockdetroit.com/preparty",
  });

  return (
    <main className="bg-yacht-charcoal text-yacht-cream">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <picture>
            <source media="(max-width: 640px)" srcSet="/pp-photo-sm.webp" />
            <img
              src="/pp-photo.webp"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "center 15%", filter: "saturate(0.9) contrast(1.05)" }}
            />
          </picture>
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 50% 40%, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.7) 100%), linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(10,10,10,1) 100%)",
            }}
          />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-5 pt-24 pb-20 md:pt-32 md:pb-28 text-center">
          <motion.p
            className="text-[#A8FF2F] text-xs md:text-sm font-black tracking-[0.3em] uppercase mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            The Official YRD Pre-Party
          </motion.p>

          <motion.img
            src="/pp-logo.webp"
            alt="Party Pupils"
            className="mx-auto w-[72%] max-w-md mb-8"
            style={{ filter: "drop-shadow(0 6px 22px rgba(168,255,47,0.55)) drop-shadow(0 4px 10px rgba(0,0,0,0.8))" }}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          />

          <motion.h1
            className="text-yacht-cream text-3xl md:text-5xl leading-tight mb-3"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 400, fontStyle: "italic" }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            High Tide on the<br />CieLo Rooftop
          </motion.h1>

          <motion.p
            className="text-yacht-cream/85 text-sm md:text-base tracking-wide mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Saturday · May 23, 2026 · Cambria Detroit
          </motion.p>

          <motion.a
            href={POSH_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#A8FF2F] hover:bg-[#bbff5c] text-yacht-charcoal font-black text-sm md:text-base tracking-wider uppercase rounded-full px-7 py-4 shadow-[0_4px_0_#7dbd1f] transition-colors"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            RSVP on Posh
            <span aria-hidden="true" className="text-lg">→</span>
          </motion.a>
        </div>
      </section>

      {/* Info grid */}
      <section className="py-16 md:py-24 px-5">
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { label: "When", value: "Saturday\nMay 23, 2026" },
            { label: "Where", value: "CieLo Rooftop\nCambria Detroit\n600 W Lafayette" },
            { label: "Cover", value: "$20 at the door\nFree for YRD VIP" },
          ].map((item) => (
            <motion.div
              key={item.label}
              className="bg-black/40 border border-white/10 rounded-2xl p-6 text-center"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-[#A8FF2F] text-[11px] font-black tracking-[0.2em] uppercase mb-3">{item.label}</p>
              <p className="text-yacht-cream text-base md:text-lg leading-snug whitespace-pre-line">{item.value}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Hosted at */}
      <section className="py-10 px-5 border-t border-white/5">
        <motion.div
          className="max-w-md mx-auto text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-yacht-cream/55 text-[10.5px] font-black tracking-[0.25em] uppercase mb-4">Hosted At</p>
          <img
            src="/cambria-logo.png"
            alt="Cambria Hotel Detroit Downtown"
            className="mx-auto h-12 md:h-14 w-auto opacity-90"
          />
          <p className="text-yacht-cream/60 text-xs md:text-sm mt-4 tracking-wide">CieLo Rooftop · 600 W Lafayette · Detroit</p>
        </motion.div>
      </section>

      {/* What's yacht house */}
      <section className="py-16 md:py-20 px-5 border-t border-white/5">
        <motion.div
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-[#A8FF2F] text-xs font-black tracking-[0.25em] uppercase mb-5">What's Yacht House?</p>
          <p
            className="text-yacht-cream text-xl md:text-2xl leading-relaxed mb-4"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic", fontWeight: 400 }}
          >
            Smooth 70s sailing anthems rebuilt for a sunset dance floor.
          </p>
          <p className="text-yacht-cream/70 text-sm md:text-base leading-relaxed">
            Party Pupils — MAX on vocals and Ryan Siegel on the boards — flip classics like Christopher Cross
            into yacht-house edits made for warm-air rooftops. Their debut compilation <em>Yacht House Summer Vol. 1</em>
            drops March 2026.
          </p>
        </motion.div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 px-5 text-center">
        <p className="text-yacht-cream/70 text-sm tracking-wider uppercase mb-5">Capacity 300 · Don't sleep</p>
        <a
          href={POSH_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 bg-[#A8FF2F] hover:bg-[#bbff5c] text-yacht-charcoal font-black text-base md:text-lg tracking-wider uppercase rounded-full px-8 py-4 shadow-[0_4px_0_#7dbd1f] transition-colors"
        >
          RSVP on Posh
          <span aria-hidden="true" className="text-xl">→</span>
        </a>
        <div className="mt-10">
          <Link to="/" className="text-yacht-cream/50 text-xs tracking-[0.2em] uppercase hover:text-yacht-cream transition-colors">
            ← Back to Yacht Rock Detroit
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default PreParty;
